import { FC, useEffect, useState } from 'react';
import { useStyles } from './styles';
import { useStore } from 'hooks/useRootStore';
import { useForm, Controller } from 'react-hook-form';
import { useAppPreloader } from 'hooks/useAppPreloader';
import { useQuery, useMutation } from 'react-query';
import { AdminUserInfoFormInput } from './types';
import { EndUserProfileInfo } from 'domain/user';
import {
  Grid,
  TextField,
  FormControl,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Button,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { observer } from 'mobx-react-lite';
import { toFormatDateTime } from 'utils';
import sampleImg from '../../../assets/sample.svg';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import MuiPhoneNumber from 'material-ui-phone-number-2';
import { Autocomplete } from '@mui/material';
import { handleCompanyUrl } from 'utils/organizationUtils';
import { useHistory } from 'react-router-dom';
import { PasswordUpdateDialog } from 'components/Dialogs';
export const EndUserProfileForm: FC = observer(() => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const history = useHistory();
  const [imgFile, setImgFile] = useState<File | undefined | string | any>('same');
  const [imageSize, setImageSize] = useState(true);
  const [imgSrc, setImgSrc] = useState<string>();
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const {
    userStore: { getProfile, endUserProfileInfo, getEndUserProfileInfo, updateEndUserProfileInfo },
    endUserSettingsFormStore: { getPortalSettings, endUserConfigSettings, CustomAttributeget, attributeKey },
    notificationStore: { openNotification },
  } = useStore();

  const portalSettingsgetReq = useQuery('portalSettingsgetReq', () => getPortalSettings(), {
    onError: () => openNotification('error', `Can't get data`),
  });
  const attributegetReq = useQuery('attributegetReq', () => CustomAttributeget(), {
    onError: () => openNotification('error', `Can't get data`),
  });

  const profileFromStorage = JSON.parse(localStorage.getItem('Profile') || 'null');
  // Check password status and show dialog if false
  useEffect(() => {
    if (profileFromStorage?.password_status === false) {
      setPasswordDialogOpen(true);
    }
  }, [profileFromStorage]);
  // const getGroupsAndDepartmentsReq = useQuery(
  //   'getGroupsAndDepartmentsReq',
  //   () => getGroupsAndDepartmentsForSelfRegister(),
  //   {
  //     onError: () => openNotification('error', `Can't get groups and departments`),
  //   }
  // );
  const getUserProfileinfo = useQuery('getUserProfileInfo', () => getEndUserProfileInfo(), {
    onError: (e) => {
      openNotification('error', `Can't get admins profile info`);
    },
  });
  const updateUserProfileInfoReq = useMutation((data: EndUserProfileInfo) => updateEndUserProfileInfo(data), {
    onSuccess: () => {
      endUserConfigSettings?.userRequest === 'true'
        ? openNotification('success', 'Profile updated successfully.')
        : openNotification(
            'success',
            `Your request to update the profile has been submitted to the administrator. Once approved, the profile will be automatically updated.`
          );
      getUserProfileinfo.refetch();
      if (endUserConfigSettings?.userRequest === 'true') {
        getProfile();
      }
      history.push(handleCompanyUrl('/reports'));
    },
    onError: (error: unknown) => {
      let errorMessage = 'An error occurred while updating your profile.';
      if (typeof error === 'object' && error !== null && 'message' in error) {
        errorMessage = (error as { message: string }).message;
      }
      openNotification('error', errorMessage);
    },
  });

  useAppPreloader(
    attributegetReq.isFetching ||
      getUserProfileinfo.isFetching ||
      updateUserProfileInfoReq.isLoading ||
      portalSettingsgetReq.isFetching
  );

  const { control, handleSubmit, setValue } = useForm<EndUserProfileInfo>();
  const onSubmit = (data: EndUserProfileInfo) => {
    const customAttributes =
      endUserProfileInfo?.customAttribute.map((attr: any) => ({
        label: attr.label,
        type: attr.type,
        name: attr.name,
        options: attr.options || null,
        value: (data as any)[attr.name] || null,
      })) || [];
    endUserProfileInfo?.customAttribute.forEach((attr: any) => {
      delete (data as any)[attr.name];
    });

    const payload: any = {
      firstName: data.firstName,
      middleName: data.middleName,
      lastName: data.lastName,
      email: data.email,
      phone: data.phone,
      personalAddress: data.personalAddress,
      organizationName: data.organizationName,
      address: data.address,
      customAttribute: customAttributes,
      userRequest: endUserConfigSettings?.userRequest,
      profileImg: imgFile || data.profileImg,
    };
    return updateUserProfileInfoReq.mutate(payload);
  };

  // Initialize form values when data is loaded
  useEffect(() => {
    if (endUserProfileInfo && !getUserProfileinfo.isFetching) {
      // Set basic fields
      setValue('firstName', endUserProfileInfo.firstName || '');
      setValue('middleName', endUserProfileInfo.middleName || '');
      setValue('lastName', endUserProfileInfo.lastName || '');
      setValue('email', endUserProfileInfo.email || '');
      setValue('phone', endUserProfileInfo.phone || '');
      setValue('personalAddress', endUserProfileInfo.personalAddress || '');
      setValue('organizationName', endUserProfileInfo.organizationName || '');
      setValue('address', endUserProfileInfo.address || '');
      setValue(
        'departments',
        Array.isArray(endUserProfileInfo.departments)
          ? endUserProfileInfo.departments.join(', ')
          : endUserProfileInfo.departments ?? ''
      );

      setValue(
        'groups',
        Array.isArray(endUserProfileInfo.groups)
          ? endUserProfileInfo.groups.join(', ')
          : endUserProfileInfo.groups ?? ''
      );
      // setValue('departments', endUserProfileInfo.departments || []);
      //   setValue('groups', endUserProfileInfo.groups || []);
      // Set custom attributes
      endUserProfileInfo.customAttribute?.forEach((attr: any) => {
        setValue(attr.name, attr.value || '');
      });
    }
  }, [endUserProfileInfo, getUserProfileinfo.isFetching, setValue]);

  const organizationBlockMock1: AdminUserInfoFormInput<EndUserProfileInfo>[] = [
    { name: 'firstName', title: 'First Name', placeholder: 'First Name', required: true },
    { name: 'middleName', title: 'Middle Name', placeholder: 'Middle Name' },
    { name: 'lastName', title: 'Last Name', placeholder: 'Last Name', required: true },
    { name: 'email', title: 'Email', placeholder: 'abc@xyz.com', required: true, disabled: true },
  ];
  const organizationBlockMock2: AdminUserInfoFormInput<EndUserProfileInfo>[] = [
    { name: 'phone', title: 'Phone:', placeholder: 'Phone number', required: false },
    { name: 'personalAddress', title: 'Personal Address', placeholder: 'Postal Address', required: false },
    // { name: 'roles', title: 'Roles:', placeholder: 'Roles', disabled: true },
    { name: 'groups', title: 'Groups', placeholder: 'Groups', disabled: true },
    { name: 'departments', title: 'Department', placeholder: 'Department', disabled: true },
    { name: 'organizationName', title: 'Organization Name', placeholder: 'Organization Name', disabled: true },
    { name: 'address', title: 'organizationAddress', placeholder: 'Billing Address', disabled: true },
  ];

  function handleChange(e: { target: { files: (Blob | MediaSource)[] | FileList | any } }) {
    if (e.target.files.length !== 0) {
      setImgFile(e.target.files ? e.target.files[0] : imgFile);
      setImgSrc(URL.createObjectURL(e.target.files[0]));
    }
  }
  useEffect(() => {
    setImgSrc(
      !getUserProfileinfo.isFetching && endUserProfileInfo?.profileUrl ? `${endUserProfileInfo?.profileUrl}` : sampleImg
    );
  }, [endUserProfileInfo?.profileUrl, getUserProfileinfo.isFetching]);

  const LightTooltip = () => {
    return (
      <div style={{ display: 'flex' }}>
        <LightbulbIcon />
        <span style={{ lineHeight: '10px' }}>Formats: jpeg, gif, bmp, png. File size (max): 2 MB</span>
      </div>
    );
  };
  return !getUserProfileinfo.isFetching ? (
    <form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
      <Grid
        className={classes.btnContainer}
        style={{
          justifyContent: isMobile ? 'center' : 'flex-end',
          margin: isMobile ? '8px 0 16px' : '16px 0 32px',
        }}
      >
        <Tooltip
          title={
            endUserConfigSettings?.profileSelfUpdate === 'false'
              ? 'Profile updates are currently disabled by the administrator.'
              : ''
          }
          arrow
        >
          <span>
            <Button
              className={classes.btn}
              type='submit'
              variant='contained'
              disabled={imageSize === false || endUserConfigSettings?.profileSelfUpdate === 'false'}
              style={{
                width: isMobile ? '100%' : '216px',
                fontSize: isMobile ? '14px' : '16px',
                height: isMobile ? '44px' : '48px',
              }}
            >
              Save Changes
            </Button>
          </span>
        </Tooltip>
      </Grid>
      <div
        className={classes.formContainer}
        style={{
          padding: isMobile ? '16px' : isTablet ? '24px' : '32px',
        }}
      >
        <div style={{ marginTop: '0px' }} className={classes.heading}>
          General Information
        </div>
        <div
          className={classes.leftCol}
          style={{
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'center' : 'center',
            gap: isMobile ? '16px' : '0px',
            marginBottom: isMobile ? '32px' : '24px',
          }}
        >
          <div
            className={classes.profileContainer}
            style={{
              marginRight: isMobile ? '0px' : '24px',
              marginBottom: isMobile ? '8px' : '0px',
            }}
          >
            <img
              className={classes.profilePicture}
              src={imgSrc}
              alt='profile'
              style={{
                width: isMobile ? '80px' : '100px',
                height: isMobile ? '80px' : '100px',
              }}
            />
          </div>
          <div
            className={classes.uploadBtnContainer}
            style={{
              marginRight: isMobile ? '0px' : '24px',
              marginBottom: isMobile ? '8px' : '0px',
              minWidth: isMobile ? '100%' : '216px',
            }}
          >
            <input
              accept='image/jpeg,image/gif,image/bmp,image/png'
              className={classes.input}
              style={{ display: 'none' }}
              id='profile-picture'
              type='file'
              name='companyLogo'
              onChange={(event) => {
                handleChange(event);
                const file = (event.target as HTMLInputElement).files?.[0];
                if (file) {
                  const img = new Image();
                  img.src = window.URL.createObjectURL(file);
                  // img.onload = function () {
                  //   const maxWidth = 400;
                  //   const maxHeight = 400;
                  //   if (img.width > maxWidth || img.height > maxHeight) {
                  //     event.target.value = '';
                  //     setImageSize(false);
                  //     openNotification('error', 'Maximum dimensions allowed are 400px x 400px.');
                  //     (event.target as HTMLInputElement).value = '';
                  //   }
                  // };
                }
              }}
              onInput={(event) => {
                const fileInput = event.target as HTMLInputElement;
                const file = fileInput.files?.[0];
                if (file) {
                  const fileType = file.type;
                  const allowedTypes = ['image/jpeg', 'image/bmp', 'image/gif', 'image/png'];
                  if (!allowedTypes.includes(fileType)) {
                    fileInput.value = '';
                    setImageSize(false);
                    openNotification('error', 'Invalid file type. Only JPEG, BMP, PNG and GIF files are allowed.');
                  } else if (file.size > 2 * 1024 * 1024) {
                    fileInput.value = '';
                    setImageSize(false);
                    openNotification('error', 'Maximum file size allowed is 2 MB.');
                  } else {
                    setImageSize(true);
                  }
                }
              }}
            />
            {/* <input
              accept='image/jpeg,image/gif,image/bmp'
              style={{ display: 'none' }}
              id='profile-picture'
              type='file'
              onChange={handleChange}
            />
            <canvas ref={canvasRef} style={{ display: 'none' }} /> 
            Your administrator will review your application and approve once you register with us.*/}

            <label htmlFor='profile-picture'>
              <Tooltip title={<LightTooltip />} classes={{ tooltip: classes.tooltip }} arrow placement='top'>
                <Button
                  component='span'
                  className={classes.link2}
                  variant='contained'
                  color='secondary'
                  style={{
                    width: isMobile ? '100%' : '216px',
                    fontSize: isMobile ? '14px' : '16px',
                    height: isMobile ? '44px' : '48px',
                  }}
                >
                  + Upload New Picture
                </Button>
              </Tooltip>
            </label>
          </div>
          <div
            className={classes.removeBtnContainer}
            style={{
              marginRight: isMobile ? '0px' : '24px',
              width: isMobile ? '100%' : '107px',
            }}
          >
            <Button
              className={classes.link}
              variant='outlined'
              onClick={() => {
                setImgFile(undefined);
                setImgSrc(sampleImg);
                setImageSize(true);
              }}
              style={{
                width: isMobile ? '100%' : '107px',
                fontSize: isMobile ? '14px' : '16px',
                height: isMobile ? '44px' : '48px',
              }}
            >
              Remove
            </Button>
          </div>
        </div>
        <div className={classes.rightCol}>
          <Grid container justify='space-between' className={classes.container}>
            <Grid className={classes.block} container>
              {organizationBlockMock1.map((item, index: number) => (
                <Controller
                  key={index}
                  name={item.name}
                  control={control}
                  rules={{
                    required: item?.required,
                    ...(item.name !== 'email' && {
                      pattern: {
                        value: /^[A-Za-z]+$/,
                        message: 'There must be no space at the start, no numeric values, and no special characters',
                      },
                    }),
                  }}
                  render={({ field, fieldState: { invalid, error } }) => (
                    <Grid container className={classes.row}>
                      <TextField
                        {...field}
                        label={item.title}
                        disabled={item.disabled || endUserConfigSettings?.profileSelfUpdate === 'false'}
                        error={invalid}
                        placeholder={item.placeholder}
                        helperText={error ? error.message : ''}
                        className={classes.input}
                      />
                    </Grid>
                  )}
                />
              ))}
              {organizationBlockMock2.map((item: any, index: number) =>
                item.name === 'phone' ? (
                  <Controller
                    key={index}
                    name={item.name}
                    control={control}
                    rules={{ required: item?.required }}
                    render={({ field, fieldState: { invalid } }) => (
                      <Grid container className={classes.row}>
                        <FormControl fullWidth>
                          <MuiPhoneNumber
                            {...field}
                            label={item.title}
                            disabled={item.disabled || endUserConfigSettings?.profileSelfUpdate === 'false'}
                            error={invalid}
                            placeholder={item.placeholder}
                            helperText={invalid ? 'Required field' : ''}
                            className={classes.input}
                            data-cy='user-phone'
                            defaultCountry={'us'}
                            variant='standard'
                            color='warning'
                          />
                        </FormControl>
                      </Grid>
                    )}
                  />
                ) : item.name === 'departments' ? (
                  <Controller
                    key={index}
                    name={item.name}
                    control={control}
                    render={({ field, fieldState: { invalid, error } }) => (
                      <Grid container className={classes.row}>
                        <TextField
                          {...field}
                          label={item.title}
                          disabled={item.disabled}
                          error={invalid}
                          placeholder={item.placeholder}
                          helperText={error ? error.message : ''}
                          className={classes.input}
                        />
                      </Grid>
                    )}
                  />
                ) : item.name === 'groups' ? (
                  <>
                    <Controller
                      key={index}
                      name={item.name}
                      control={control}
                      render={({ field, fieldState: { invalid, error } }) => (
                        <Grid container className={classes.row}>
                          <TextField
                            {...field}
                            label={item.title}
                            disabled={item.disabled}
                            error={invalid}
                            placeholder={item.placeholder}
                            helperText={error ? error.message : ''}
                            className={classes.input}
                          />
                        </Grid>
                      )}
                    />
                  </>
                ) : (
                  <Controller
                    key={index}
                    name={item.name}
                    control={control}
                    rules={{ required: item?.required }}
                    render={({ field, fieldState: { invalid } }) => (
                      <Grid container className={classes.row}>
                        <TextField
                          {...field}
                          label={item.title}
                          disabled={item.disabled}
                          error={invalid}
                          placeholder={item.placeholder}
                          helperText={invalid ? 'Required field' : ''}
                          className={classes.input}
                        />
                      </Grid>
                    )}
                  />
                )
              )}
              {attributeKey?.map((attr: any) => (
                <>
                  {attr.type === 'date' ? (
                    <Controller
                      key={attr.name}
                      name={attr.name}
                      defaultValue={
                        endUserProfileInfo?.customAttribute.find((attri: any) => attri.name === attr.name)?.value || ''
                      }
                      control={control}
                      render={({ field, fieldState }) => (
                        <Grid container className={classes.row}>
                          <TextField
                            {...field}
                            label={attr.label}
                            type='date'
                            disabled={endUserConfigSettings?.profileSelfUpdate === 'false'}
                            InputLabelProps={{ shrink: true }}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                            className={classes.input}
                          />
                        </Grid>
                      )}
                    />
                  ) : attr.type === 'select' ? (
                    <>
                      <Controller
                        key={attr.name}
                        name={attr.name}
                        control={control}
                        defaultValue={
                          endUserProfileInfo?.customAttribute.find((attri: any) => attri.name === attr.name)?.value ||
                          ''
                        }
                        disabled={endUserConfigSettings?.profileSelfUpdate === 'false'}
                        render={({ field }) => (
                          <Grid container className={classes.row}>
                            <Autocomplete
                              {...field}
                              className={classes.input}
                              options={attr.options || []}
                              getOptionLabel={(option) => option}
                              value={attr.options?.find((opt: any) => opt === field.value) || null}
                              onChange={(_, newValue) => field.onChange(newValue)}
                              renderInput={(params) => <TextField {...params} variant='outlined' label={attr.label} />}
                            />
                          </Grid>
                        )}
                      />
                    </>
                  ) : attr.type === 'checkbox' ? (
                    <>
                      <Grid className={classes.row} direction='column'>
                        <p>{attr.label}</p>
                        {Array.isArray(attr.options) ? (
                          <Controller
                            name={attr.name}
                            control={control}
                            disabled={endUserConfigSettings?.profileSelfUpdate === 'false'}
                            defaultValue={(() => {
                              const foundValue = endUserProfileInfo?.customAttribute.find(
                                (attri: any) => attri.name === attr.name
                              )?.value;
                              return typeof foundValue === 'string' ? foundValue : '';
                            })()}
                            render={({ field }) => {
                              const selectedValues = field.value
                                ? field.value
                                    .split(',')
                                    .map((v: string) => v.trim())
                                    .filter(Boolean)
                                : [];
                              return attr.options.map((option: string) => {
                                const isChecked = selectedValues.includes(option);
                                return (
                                  <FormControlLabel
                                    key={option}
                                    control={
                                      <Checkbox
                                        checked={isChecked}
                                        disabled={endUserConfigSettings?.profileSelfUpdate === 'false'}
                                        onChange={(e) => {
                                          const newValues = e.target.checked
                                            ? [...selectedValues, option]
                                            : selectedValues.filter((val: string) => val !== option);
                                          field.onChange(newValues.join(','));
                                        }}
                                      />
                                    }
                                    label={option}
                                  />
                                );
                              });
                            }}
                          />
                        ) : (
                          <p>No options available</p>
                        )}
                      </Grid>
                    </>
                  ) : (
                    <Controller
                      key={attr.name}
                      name={attr.name}
                      defaultValue={
                        endUserProfileInfo?.customAttribute.find((attri: any) => attri.name === attr.name)?.value || ''
                      }
                      control={control}
                      render={({ field, fieldState: { invalid, error } }) => (
                        <Grid container className={classes.row}>
                          <TextField
                            {...field}
                            label={attr.label}
                            className={classes.input}
                            error={invalid}
                            disabled={endUserConfigSettings?.profileSelfUpdate === 'false'}
                            helperText={invalid ? (error?.type === 'pattern' ? '' : 'Required field') : ''}
                          />
                        </Grid>
                      )}
                    />
                  )}
                </>
              ))}
              <Controller
                name='lastLogin'
                defaultValue={
                  endUserProfileInfo?.lastLogin ? toFormatDateTime(endUserProfileInfo?.lastLogin.toString()) : '-'
                }
                control={control}
                render={({ field, fieldState: { invalid } }) => (
                  <Grid container className={classes.row}>
                    <TextField
                      {...field}
                      label='Last Login'
                      disabled
                      error={invalid}
                      placeholder='-'
                      helperText={invalid ? 'Required field' : ''}
                      className={classes.input}
                    />
                  </Grid>
                )}
              />
            </Grid>
          </Grid>
        </div>
      </div>
      <PasswordUpdateDialog openModal={passwordDialogOpen} handleClose={() => setPasswordDialogOpen(false)} />
    </form>
  ) : null;
});
